/**
 * Generic prev/next scroll buttons for any horizontally-scrolling rail.
 * Works with [data-wifi-carousel] containing a [data-carousel-track] (the
 * scrollable element itself) and [data-carousel-prev]/[data-carousel-next]
 * buttons. RTL-aware: reads the computed direction so "next" always advances
 * in reading order, whether the row scrolls in reverse under RTL or not.
 * No external library — plain scrollBy.
 *
 * Auto scroll: opt-in per instance via [data-autoplay] (+ optional
 * [data-autoplay-speed] in ms). Pauses on hover/touch/focus so a visitor
 * scanning cards never fights the animation, and stays off entirely under
 * prefers-reduced-motion.
 */
document.querySelectorAll('[data-wifi-carousel]').forEach((carousel) => {
  const track = carousel.querySelector('[data-carousel-track]');
  const prevBtn = carousel.querySelector('[data-carousel-prev]');
  const nextBtn = carousel.querySelector('[data-carousel-next]');
  if (!track) return;

  const isRtl = getComputedStyle(carousel).direction === 'rtl';
  const getStep = () => {
    const card = track.querySelector(':scope > *');
    return card ? card.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
  };
  const scrollByCard = (directionSign) => {
    track.scrollBy({ left: directionSign * getStep() * (isRtl ? -1 : 1), behavior: 'smooth' });
  };

  // Some instances render with prev/next scrolling swapped relative to
  // their visual position (RTL layout quirk); opt in per-instance to flip.
  const reverseNav = 'reverseNav' in carousel.dataset;
  prevBtn?.addEventListener('click', () => scrollByCard(reverseNav ? 1 : -1));
  nextBtn?.addEventListener('click', () => scrollByCard(reverseNav ? -1 : 1));

  if (!('autoplay' in carousel.dataset)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const speed = parseInt(carousel.dataset.autoplaySpeed, 10) || 3500;
  let timer = null;
  let paused = false;

  // Modern browsers report RTL scrollLeft as 0..-maxScroll, so abs() gives a
  // direction-agnostic "distance scrolled from the start" in both modes.
  const atEnd = () => {
    const maxScroll = track.scrollWidth - track.clientWidth;
    return Math.abs(track.scrollLeft) >= maxScroll - 4;
  };

  const tick = () => {
    if (paused) return;
    if (atEnd()) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      scrollByCard(1);
    }
  };

  const start = () => {
    stop();
    timer = window.setInterval(tick, speed);
  };
  const stop = () => {
    if (timer) window.clearInterval(timer);
    timer = null;
  };

  const pause = () => {
    paused = true;
  };
  const resume = () => {
    paused = false;
  };

  carousel.addEventListener('mouseenter', pause);
  carousel.addEventListener('mouseleave', resume);
  carousel.addEventListener('touchstart', pause, { passive: true });
  carousel.addEventListener('touchend', resume);
  carousel.addEventListener('focusin', pause);
  carousel.addEventListener('focusout', resume);

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) start();
        else stop();
      });
    },
    { threshold: 0.3 }
  );
  io.observe(carousel);
});
